import {
  DEFAULT_GIT_TEXT_GENERATION_MODEL_BY_PROVIDER,
  type ModelSelection,
  type ProviderKind,
  type ServerProvider,
} from "@cmmd-forge/contracts";
import { belongsToDifferentProvider, resolveSelectableModel } from "@cmmd-forge/shared/model";
import { getComposerProviderState } from "./components/chat/composerProviderState";
import { UnifiedSettings } from "@cmmd-forge/contracts/settings";
import {
  getDefaultServerModel,
  getProviderModels,
  resolveSelectableProvider,
} from "./providerModels";
import { isOfferableProvider } from "./providerPickerAvailability";
import type { CmmdOrgProviderPolicy } from "@cmmd-forge/contracts";

export interface AppModelOption {
  slug: string;
  name: string;
  isCustom: boolean;
  readonly isDefault?: boolean | undefined;
  readonly isLegacy?: boolean | undefined;
}

export function getAppModelOptions(
  _settings: UnifiedSettings,
  providers: ReadonlyArray<ServerProvider>,
  provider: ProviderKind,
  _selectedModel?: string | null,
): AppModelOption[] {
  return getProviderModels(providers, provider).map(
    ({ slug, name, isCustom, isDefault, isLegacy }) => ({
      slug,
      name,
      isCustom,
      isDefault,
      isLegacy,
    }),
  );
}

export function resolveAppModelSelection(
  provider: ProviderKind,
  settings: UnifiedSettings,
  providers: ReadonlyArray<ServerProvider>,
  selectedModel: string | null | undefined,
  policy?: CmmdOrgProviderPolicy | null,
): string {
  // Resolving an explicit model does not authorize execution. Availability is
  // checked by the caller's CMMD-aware provider path, so a missing policy here
  // must not replace a saved selection with another provider's default.
  const requested = resolveSelectableModel(
    provider,
    selectedModel,
    getAppModelOptions(settings, providers, provider, selectedModel),
  );
  if (requested && !belongsToDifferentProvider(requested, provider)) return requested;
  const resolvedProvider = resolveAppSelectableProvider(providers, provider, policy);
  const options = getAppModelOptions(settings, providers, resolvedProvider, selectedModel);
  const resolved =
    resolveSelectableModel(resolvedProvider, selectedModel, options) ??
    getDefaultServerModel(providers, resolvedProvider);

  // A MODEL resolver must never hand back a model belonging to a provider other
  // than the one it was asked about.
  //
  // `resolveAppSelectableProvider` silently substitutes codex whenever the
  // requested provider is not offerable, and `policy` is optional — every call
  // site that omits it gets DEFAULT_ORG_PROVIDER_POLICY, which withholds Claude.
  // So asking for ("claudeAgent", "claude-sonnet-5") with no policy resolved
  // against CODEX's catalogue and returned "gpt-5.6-sol", while the caller kept
  // provider "claudeAgent". That pair spawns `claude --model gpt-5.6-sol`, which
  // Claude Code rejects as an auth failure, and it is what production threads
  // 67849623, 8e3f11f5 and f0b8cf4a were dispatched with.
  //
  // Healing to the requested provider's own default keeps the pair coherent.
  // Whether that provider may be used at all is the picker's decision, not this
  // function's, and a refusal there is legible where a foreign pair is not.
  if (provider && belongsToDifferentProvider(resolved, provider)) {
    return getDefaultServerModel(providers, provider);
  }
  return resolved;
}

function resolveAppSelectableProvider(
  providers: ReadonlyArray<ServerProvider>,
  provider: ProviderKind | null | undefined,
  policy?: CmmdOrgProviderPolicy | null,
): ProviderKind {
  if (!provider || !isOfferableProvider(provider, policy)) {
    return resolveSelectableProvider(providers, "codex");
  }
  return resolveSelectableProvider(providers, provider);
}

export function getCustomModelOptionsByProvider(
  settings: UnifiedSettings,
  providers: ReadonlyArray<ServerProvider>,
  selectedProvider?: ProviderKind | null,
  selectedModel?: string | null,
): Record<ProviderKind, ReadonlyArray<AppModelOption>> {
  return {
    codex: getAppModelOptions(
      settings,
      providers,
      "codex",
      selectedProvider === "codex" ? selectedModel : undefined,
    ),
    claudeAgent: getAppModelOptions(
      settings,
      providers,
      "claudeAgent",
      selectedProvider === "claudeAgent" ? selectedModel : undefined,
    ),
    ollama: getAppModelOptions(
      settings,
      providers,
      "ollama",
      selectedProvider === "ollama" ? selectedModel : undefined,
    ),
    cmmd: getAppModelOptions(
      settings,
      providers,
      "cmmd",
      selectedProvider === "cmmd" ? selectedModel : undefined,
    ),
    apex: getAppModelOptions(
      settings,
      providers,
      "apex",
      selectedProvider === "apex" ? selectedModel : undefined,
    ),
    cursor: getAppModelOptions(
      settings,
      providers,
      "cursor",
      selectedProvider === "cursor" ? selectedModel : undefined,
    ),
    opencode: getAppModelOptions(
      settings,
      providers,
      "opencode",
      selectedProvider === "opencode" ? selectedModel : undefined,
    ),
  };
}

export function resolveAppModelSelectionState(
  settings: UnifiedSettings,
  providers: ReadonlyArray<ServerProvider>,
  policy?: CmmdOrgProviderPolicy | null,
): ModelSelection {
  const selection = settings.textGenerationModelSelection ?? {
    provider: "codex" as const,
    model: DEFAULT_GIT_TEXT_GENERATION_MODEL_BY_PROVIDER.codex,
  };
  const provider = resolveAppSelectableProvider(providers, selection.provider, policy);

  // When the provider changed due to fallback (e.g. selected provider was disabled),
  // don't carry over the old provider's model — use the fallback provider's default.
  const selectedModel = provider === selection.provider ? selection.model : null;
  const model = resolveAppModelSelection(provider, settings, providers, selectedModel, policy);
  const { modelOptionsForDispatch } = getComposerProviderState({
    provider,
    model,
    models: getProviderModels(providers, provider),
    prompt: "",
    modelOptions: {
      [provider]: provider === selection.provider ? selection.options : undefined,
    },
  });

  return {
    provider,
    model,
    ...(modelOptionsForDispatch ? { options: modelOptionsForDispatch } : {}),
  } as ModelSelection;
}
